import { announceIfOrgWrite } from "./orgSync";

/**
 * API client for Nuance Forge backend (Azure Functions).
 * In production, calls go directly to the Function App URL.
 * In dev, Vite proxy handles /api -> local function host.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

/** Active project filter — appended to all API requests when set. */
let _activeProjectId: string | null = null;

export function setApiProjectFilter(projectId: string | null) {
  _activeProjectId = projectId;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown,
  ) {
    super(`API error ${status}: ${statusText}`);
    this.name = "ApiError";
  }
}

/** Returns true for errors worth retrying (5xx, network failures, timeouts). */
function isRetryable(err: unknown): boolean {
  if (err instanceof ApiError) return err.status >= 500 || err.status === 0;
  if (err instanceof TypeError) return true; // network error
  return false;
}

/** Sleep for ms with jitter. */
const sleep = (ms: number) =>
  new Promise((r) => setTimeout(r, ms + Math.random() * 100));

const MAX_RETRIES = 2;
const RETRY_BASE_MS = 500;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  timeoutMs = 60_000,
  retry = true,
): Promise<T> {
  // FORGE-197: `retry = false` for calls that are NOT safe to repeat.
  // A timeout surfaces as ApiError(status 0), which isRetryable() treats as
  // retryable — correct for a read, wrong for a POST that starts work. A slow
  // data repull would otherwise turn one click into three concurrent
  // server-side pulls of the same window, which is precisely the duplicate
  // work the repull feature exists to avoid.
  const maxRetries = retry ? MAX_RETRIES : 0;
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      await sleep(RETRY_BASE_MS * Math.pow(2, attempt - 1));
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    const opts: RequestInit = {
      method,
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      // Prevent the browser from serving stale cached responses for GETs.
      // Symptom we hit: after migrations land new SOURCE_CONFIGS rows,
      // operators had to hard-refresh Forge → Data Sources to see them
      // because the previous list was cached. 'no-store' forces every
      // call to hit the Function App fresh — auth + retry logic still
      // handles transient failures so we don't risk a flicker.
      cache: "no-store",
    };
    if (body !== undefined) {
      opts.body = JSON.stringify(body);
    }
    // Append project filter if active
    let resolvedPath = path;
    if (_activeProjectId) {
      resolvedPath += resolvedPath.includes("?")
        ? `&project_id=${encodeURIComponent(_activeProjectId)}`
        : `?project_id=${encodeURIComponent(_activeProjectId)}`;
    }

    let res: Response;
    try {
      res = await fetch(`${API_BASE}/api${resolvedPath}`, opts);
    } catch (err) {
      clearTimeout(timeout);
      if (err instanceof DOMException && err.name === "AbortError") {
        lastError = new ApiError(
          0,
          `Request timed out after ${Math.round(timeoutMs / 1000)}s`,
        );
      } else {
        lastError = err;
      }
      if (attempt < maxRetries && isRetryable(lastError)) continue;
      throw lastError;
    }
    clearTimeout(timeout);
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
      if (res.status === 401) {
        console.warn("[api] 401 on", path, "— redirecting to login");
        // Clear any stale state and redirect to AAD login
        // Small delay so the console.warn is visible for debugging
        setTimeout(() => {
          window.location.href =
            "/.auth/login/aad?post_login_redirect_uri=" +
            encodeURIComponent(window.location.pathname);
        }, 100);
      }
      lastError = new ApiError(res.status, res.statusText, parsed);
      // Only retry on 5xx server errors, not 4xx client errors
      if (attempt < maxRetries && res.status >= 500) continue;
      throw lastError;
    }
    if (res.status === 204) {
      announceIfOrgWrite(method, path);
      return undefined as T;
    }
    const data = (await res.json()) as T;
    // a saved change to people / clients / groups / access: the other
    // Organization pages reload (services/orgSync.ts)
    announceIfOrgWrite(method, path);
    return data;
  }

  throw lastError;
}

export const api = {
  get: <T>(path: string, timeoutMs?: number) =>
    request<T>("GET", path, undefined, timeoutMs),
  post: <T>(path: string, body?: unknown, timeoutMs?: number) =>
    request<T>("POST", path, body, timeoutMs),
  /** POST that is NOT auto-retried — for calls that start work (see request()). */
  postOnce: <T>(path: string, body?: unknown, timeoutMs?: number) =>
    request<T>("POST", path, body, timeoutMs, false),
  put: <T>(path: string, body?: unknown, timeoutMs?: number) =>
    request<T>("PUT", path, body, timeoutMs),
  patch: <T>(path: string, body?: unknown, timeoutMs?: number) =>
    request<T>("PATCH", path, body, timeoutMs),
  delete: <T>(path: string, timeoutMs?: number) =>
    request<T>("DELETE", path, undefined, timeoutMs),

  /** Upload files via multipart/form-data (no progress tracking) */
  upload: async <T>(
    path: string,
    files: File[],
    extraFields?: Record<string, string>,
  ): Promise<T> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 300_000);
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    if (extraFields) {
      Object.entries(extraFields).forEach(([k, v]) => form.append(k, v));
    }
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/api${path}`, {
        method: "POST",
        body: form,
        signal: controller.signal,
      });
    } catch (err) {
      clearTimeout(timeout);
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new ApiError(0, "Upload timed out after 5 minutes");
      }
      throw err;
    }
    clearTimeout(timeout);
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
      throw new ApiError(res.status, res.statusText, parsed);
    }
    return res.json();
  },

  /**
   * SAS-based upload: presign -> upload to blob -> start processing.
   * Bypasses Azure Functions entirely for file transfer.
   */
  uploadWithProgress: <T>(
    _path: string,
    files: File[],
    onProgress: (pct: number) => void,
    extraFields?: Record<string, string>,
  ): { promise: Promise<T>; abort: () => void } => {
    const controller = new AbortController();

    const promise = (async (): Promise<T> => {
      // Step 1: Get SAS URLs from backend
      const presignBody: Record<string, unknown> = {
        filenames: files.map((f) => f.name),
      };
      if (extraFields?.client) presignBody.client = extraFields.client;
      if (extraFields?.broker) presignBody.broker = extraFields.broker;
      if (extraFields?.retailer) presignBody.retailer = extraFields.retailer;
      if (extraFields?.channel) presignBody.channel = extraFields.channel;
      if (extraFields?.mismatch_decisions) {
        try {
          presignBody.mismatch_decisions = JSON.parse(
            extraFields.mismatch_decisions,
          );
        } catch {
          /* ignore */
        }
      }

      const presign = await request<{
        batch_id: string;
        total_files: number;
        files: Array<{
          filename: string;
          load_id: string;
          blob_path: string;
          sas_url: string;
        }>;
        overrides: Record<string, string | null>;
        mismatch_decisions: Record<string, unknown>;
        project_id: string | null;
      }>("POST", "/upload/presign", presignBody);

      // Step 2: Upload each file directly to Azure Blob Storage via SAS URL
      let uploaded = 0;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const sasInfo = presign.files[i];
        if (!sasInfo) continue;

        const res = await fetch(sasInfo.sas_url, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": "application/octet-stream",
          },
          body: file,
          signal: controller.signal,
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new ApiError(
            res.status,
            `Blob upload failed for ${file.name}`,
            text,
          );
        }

        uploaded++;
        onProgress(Math.round((uploaded / files.length) * 100));
      }

      // Step 3: Tell backend to start processing
      const startBody: Record<string, unknown> = {
        files: presign.files.map((f) => ({
          filename: f.filename,
          load_id: f.load_id,
          blob_path: f.blob_path,
        })),
        overrides: presign.overrides,
      };
      if (
        presign.mismatch_decisions &&
        Object.keys(presign.mismatch_decisions).length > 0
      ) {
        startBody.mismatch_decisions = presign.mismatch_decisions;
      }
      if (presign.project_id) {
        startBody.project_id = presign.project_id;
      }

      const result = await request<T>(
        "POST",
        `/upload/batch/${presign.batch_id}/start`,
        startBody,
      );
      return result;
    })();

    return { promise, abort: () => controller.abort() };
  },
};
